import { createAsyncThunk } from '@reduxjs/toolkit';
import { authService, userService, progressService } from '@/services';
import { RootState } from '@/types/common';
import { LevelSettingsResponse } from '@/types/response/user';
import { ResponseMissingGooglePermissions } from '@/types/response';
import { jwtDecode } from 'jwt-decode';
import { registerService } from '@/services/register';
import { CreateUserDetails } from '@/types/register';
import userUtils from '@/utils/user';

const userThunks = {
  userInfo: createAsyncThunk(
    'user/me',
    async (
      {
        retryCount,
        trySignup,
      }: {
        retryCount: number;
        trySignup?: boolean;
      },
      { getState, rejectWithValue }
    ) => {
      try {
        const state = getState() as RootState;
        let error: any;

        for (let i = 0; i < retryCount; i++) {
          try {
            const user = await authService.me(state.auth?.userId);
            return user?.data;
          } catch (ex) {
            error = ex;
          }
        }

        const responseCode = error?.response?.status || 0;
        const responseMessage = (error?.response?.data?.message || '').toLowerCase();
        const cognitoUserPoolData = userUtils.getCognitoUserPoolData();
        const lastAuthUser =
          localStorage.getItem(
            `CognitoIdentityServiceProvider.${cognitoUserPoolData.awsCognitoClientId}.LastAuthUser`
          ) || '';
        const idToken = localStorage.getItem(
          `CognitoIdentityServiceProvider.${cognitoUserPoolData.awsCognitoClientId}.${lastAuthUser}.idToken`
        );

        if (
          trySignup &&
          responseCode === 404 &&
          responseMessage.includes('the requested user does not exist') &&
          idToken
        ) {
          const decoded = jwtDecode<{
            registrationQuery: string;
            role: string;
            email: string;
            name: string;
            given_name: string;
            family_name: string;
          }>(idToken);
          if (
            decoded.registrationQuery &&
            decoded.email &&
            decoded.given_name &&
            decoded.family_name
          ) {
            const registrationParams = new URLSearchParams(decoded.registrationQuery);
            const dealId = registrationParams.get('dealId') || '';
            const organizationId = registrationParams.get('organizationId') || '';
            const userRole = state.auth?.userRole;
            let isSignup: boolean = false;

            localStorage.setItem(
              'register-registration_data',
              JSON.stringify({
                dealId,
                organizationId,
              })
            );

            const userDetails: CreateUserDetails = {
              firstName: decoded.given_name,
              lastName: decoded.family_name,
              userId: state.auth?.userId || '',
              email: decoded.email,
            };

            if (userRole === 'admin' && dealId) {
              try {
                const userRegister = (await registerService.createAdmin(userDetails))?.data.data;

                if (userRegister) {
                  isSignup = true;
                }
              } catch (ex) {}
            } else if (userRole === 'teacher' && organizationId) {
              try {
                const userRegister =
                  (await registerService.createTeacher(userDetails))?.data.data || {};

                if (userRegister) {
                  isSignup = true;
                }
              } catch (ex) {}
            }

            if (isSignup) {
              try {
                const user = await authService.me(state.auth?.userId);
                return user?.data;
              } catch (ex) {
                error = ex;
              }
            }
          }
        }

        throw error;
      } catch (error) {
        if ((error as any)?.response?.data?.data?.missingScopes?.length > 0) {
          const missingGooglePermissions: ResponseMissingGooglePermissions = {
            message: (error as any)?.response?.data?.message || '',
            missingPermissions: (error as any)?.response?.data?.data?.missingScopes,
          };
          return rejectWithValue(missingGooglePermissions);
        } else {
          return rejectWithValue({ error });
        }
      }
    }
  ),
  fetchLevels: createAsyncThunk('user/level', async (_, { getState }) => {
    try {
      const state = getState() as RootState;
      const levelsResponse: LevelSettingsResponse | null = await userService.fetchLevelSettings(
        state.auth.user
      );
      return levelsResponse?.data;
    } catch (e) {
      console.log(e);
    }
  }),
  fetchLanguages: createAsyncThunk('user/language', async (_, { getState }) => {
    try {
      const state = getState() as RootState;
      const languages = await userService.fetchLanguageSettings(state.auth.user);
      return languages?.data;
    } catch (e) {
      console.log(e);
    }
  }),
  setLanguageSettings: createAsyncThunk(
    'user/languageSettings',
    async (interfaceLanguage: string, { getState }) => {
      try {
        const state = getState() as RootState;

        await userService.setUserLanguageSettings(state.auth.user, interfaceLanguage);
      } catch (e) {
        console.log(e);
      }
    }
  ),
  signOut: createAsyncThunk('user/signout', async (_, { getState }) => {
    try {
      const state = getState() as RootState;

      await userService.setUserLogOut(state.auth.user);
    } catch (e) {
      console.log(e);
    }
  }),
  fetchImprovableRecentActivities: createAsyncThunk(
    'user/improvableRecentActivities',
    async (_, { getState, rejectWithValue }) => {
      try {
        const state = getState() as RootState;
        const improvableRecentActivities = await progressService.fetchImprovableRecentActivities(
          state.auth.user
        );
        return JSON.parse(JSON.stringify(improvableRecentActivities));
      } catch (e) {
        console.error(e);
        return rejectWithValue(e instanceof Error ? e.message : 'An error occurred');
      }
    }
  ),
  fetchAchievementOfTheWeek: createAsyncThunk(
    'user/achievementOfTheWeek',
    async (_, { getState, rejectWithValue }) => {
      try {
        const state = getState() as RootState;
        const achievementOfTheWeek = await progressService.fetchAchievementOfTheWeek(
          state.auth.user
        );
        return JSON.parse(JSON.stringify(achievementOfTheWeek));
      } catch (e) {
        console.error(e);
        return rejectWithValue(e instanceof Error ? e.message : 'An error occurred');
      }
    }
  ),
};

export default userThunks;
